import { Link } from "react-router-dom";
import type { Comic } from "@/types/comic";
import { formatCount, formatDate, statusLabel } from "@/lib/format";

interface ComicCardProps {
  comic: Comic;
}

export default function ComicCard({ comic }: ComicCardProps) {
  const latest = comic.chapters[comic.chapters.length - 1];
  const isCompleted = comic.status === "completed";

  return (
    <Link
      to={`/detail/${comic.id}`}
      className="group block rounded-lg overflow-hidden bg-background-100 border border-background-200 hover:border-primary-400 transition-all duration-300"
    >
      <div className="relative w-full aspect-[3/4] overflow-hidden">
        <img
          src={comic.cover}
          alt={`${comic.title} 高清漫画封面`}
          title={`${comic.title} JMcomic2 高清漫画在线阅读`}
          className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-background-950/10 to-transparent" />
        <span
          className={`absolute top-2 left-2 px-2 py-0.5 rounded-md text-xs font-medium ${
            isCompleted
              ? "bg-secondary-100/90 text-secondary-900"
              : "bg-accent-500/90 text-background-50"
          }`}
        >
          {statusLabel(comic.status)}
        </span>
        <span className="absolute top-2 right-2 px-2 py-0.5 rounded-md text-xs font-medium bg-background-950/70 text-primary-400 flex items-center gap-1">
          <i className="ri-star-fill text-[10px]" />
          {comic.rating.toFixed(1)}
        </span>
        <div className="absolute bottom-2 left-2 right-2">
          <p className="text-xs text-foreground-800 flex items-center gap-1">
            <i className="ri-history-line" />
            更新至第 {latest?.number ?? 1} 话
          </p>
        </div>
      </div>

      <div className="p-3">
        <h3 className="font-heading font-semibold text-sm text-foreground-950 truncate group-hover:text-primary-500 transition-colors">
          {comic.title}
        </h3>
        <p className="mt-1.5 text-xs text-foreground-500 line-clamp-2 leading-relaxed h-8">
          {comic.description}
        </p>
        <div className="mt-2.5 flex items-center justify-between gap-2 text-xs text-foreground-500">
          <div className="flex items-center gap-2 min-w-0">
            <span className="flex items-center gap-1 shrink-0">
              <i className="ri-eye-line" />
              {formatCount(comic.views)}
            </span>
            <span className="shrink-0">{formatDate(comic.updatedAt)}</span>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {comic.genres.slice(0, 3).map((genre) => (
            <span key={genre} className="px-1.5 py-0.5 rounded text-[11px] bg-secondary-100 text-secondary-900">
              {genre}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}